import { useEffect, useState } from 'react';
import { neoApi } from '../api';
import { Page, Card, Button } from '../ui';

/**
 * Padrões de Agente (Admin) — as bases que compõem TODO agente de TODO projeto:
 * guardrails universais (segurança) + padrão por tipo (redator, pesquisador…). O
 * Planejador junta a base escolhida com o específico do projeto. Editar aqui vale
 * imediatamente pros próximos agentes criados.
 */

interface BaseItem {
  name: string;
  title: string;
  guardrail: boolean;
}

const NEW_TEMPLATE = (title: string) =>
  `# Padrão de ${title} — NEO Pilots\n\nVocê é um ${title.toLowerCase()} da NEO Pilots. Siga este padrão além do específico do projeto.\n\n## Regras\n- (escreva aqui as regras deste tipo de agente)\n`;

export function AgentBases() {
  const [bases, setBases] = useState<BaseItem[]>([]);
  const [sel, setSel] = useState<string | null>(null);
  const [content, setContent] = useState('');
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);

  async function loadList() {
    try {
      const r = await neoApi.agentBases();
      setBases(r.bases);
      if (!sel && r.bases[0]) void open(r.bases[0].name);
    } catch {
      /* silencioso */
    }
  }
  useEffect(() => {
    void loadList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function open(name: string) {
    setSel(name);
    setMsg(null);
    try {
      const r = await neoApi.agentBase(name);
      setContent(r.content);
      setDirty(false);
    } catch {
      setContent('');
    }
  }

  async function save() {
    if (!sel) return;
    setSaving(true);
    setMsg(null);
    try {
      await neoApi.saveAgentBase(sel, content);
      setDirty(false);
      setMsg('Salvo — vale pros próximos agentes criados.');
    } catch {
      setMsg('Não consegui salvar.');
    } finally {
      setSaving(false);
    }
  }

  async function novoTipo() {
    const nome = prompt('Nome do novo tipo (ex: revisor, social, designer):');
    if (!nome) return;
    const key = nome.trim().toLowerCase().replace(/[^a-z0-9_-]+/g, '-').replace(/^-+|-+$/g, '');
    if (!key) return;
    await neoApi.saveAgentBase(key, NEW_TEMPLATE(nome.trim()));
    await loadList();
    void open(key);
  }

  return (
    <Page
      title="Padrões de Agente"
      subtitle="Regras e qualidade que valem pra todo agente de todo projeto. O sistema junta a base com o específico de cada projeto."
      actions={<Button variant="ghost" onClick={novoTipo}>+ Novo tipo</Button>}
    >
      <div style={{ display: 'flex', gap: 'var(--space-4)', alignItems: 'flex-start' }}>
        {/* lista */}
        <div style={{ width: 220, flexShrink: 0, display: 'flex', flexDirection: 'column', gap: 2 }}>
          {bases.map((b) => (
            <button
              key={b.name}
              onClick={() => void open(b.name)}
              style={{
                textAlign: 'left',
                padding: 'var(--space-2) var(--space-3)',
                borderRadius: 'var(--radius-md, 8px)',
                border: '1px solid ' + (sel === b.name ? 'var(--color-accent)' : 'transparent'),
                background: sel === b.name ? 'var(--color-bg-subtle)' : 'transparent',
                color: 'var(--color-fg)',
                cursor: 'pointer',
                fontSize: 'var(--font-size-sm)',
              }}
            >
              {b.guardrail ? '🛡️ ' : '📐 '}
              {b.title}
              <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-fg-muted)' }}>{b.name}</div>
            </button>
          ))}
          {bases.length === 0 && <div style={{ color: 'var(--color-fg-muted)', fontSize: 'var(--font-size-sm)' }}>Carregando…</div>}
        </div>

        {/* editor */}
        <Card size="normal" className="" >
          <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 'var(--space-2)', width: 'min(720px, 60vw)' }}>
            {sel ? (
              <>
                <textarea
                  value={content}
                  onChange={(e) => {
                    setContent(e.target.value);
                    setDirty(true);
                  }}
                  spellCheck={false}
                  style={{
                    width: '100%',
                    minHeight: '52vh',
                    resize: 'vertical',
                    padding: 'var(--space-3)',
                    borderRadius: 'var(--radius-md, 8px)',
                    border: '1px solid var(--color-border)',
                    background: 'var(--color-bg)',
                    color: 'var(--color-fg)',
                    fontFamily: 'var(--font-mono, monospace)',
                    fontSize: 'var(--font-size-sm)',
                    lineHeight: 1.6,
                  }}
                />
                <div style={{ display: 'flex', gap: 'var(--space-2)', alignItems: 'center' }}>
                  <Button variant="primary" disabled={saving || !dirty} onClick={save}>
                    {saving ? 'Salvando…' : 'Salvar'}
                  </Button>
                  {msg && <span style={{ fontSize: 'var(--font-size-sm)', color: 'var(--color-fg-muted)' }}>{msg}</span>}
                </div>
              </>
            ) : (
              <div style={{ color: 'var(--color-fg-muted)' }}>Selecione um padrão à esquerda.</div>
            )}
          </div>
        </Card>
      </div>
    </Page>
  );
}
